import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';
import type { Virtualizer } from '@tanstack/react-virtual';
import type { GroupedRow } from './useGroupedData';
import { isGroupRow } from './useGroupedData';

/**
 * Represents a logical "row group" for virtualization purposes.
 * A single virtual item may map to multiple DOM <tr> elements
 * (e.g., a parent row + sub-row header + N sub-row children).
 */
export interface VirtualRowGroup {
  /** Index in the original renderData array */
  dataIndex: number;
  /** Whether this is a group header (from useGroupedData), a data row, or a skeleton placeholder */
  type: 'group' | 'data' | 'skeleton';
  /** The raw row data — null for skeleton rows */
  row: Record<string, unknown> | GroupedRow | null;
  /** Estimated height in px for this row group (parent + expanded sub-rows) */
  estimatedHeight: number;
}

const EMPTY_ITEMS: ReadonlyArray<{ index: number; start: number; size: number }> = [];
const EMPTY_ROW_GROUPS: VirtualRowGroup[] = [];

interface BaseVirtualRowsOptions {
  scrollContainerRef: React.RefObject<HTMLElement | null>;
  enabled: boolean;
  estimatedRowHeight: number;
  overscan: number;
  expandedRows?: ReadonlyMap<string, boolean> | Set<string>;
  estimatedSubRowHeight?: number;
  subRowDataField?: string;
  rowIdField?: string;
}

/** Standard mode — all rows are in memory via renderData */
export interface UseVirtualRowsOptions extends BaseVirtualRowsOptions {
  renderData: ReadonlyArray<Record<string, unknown> | GroupedRow>;
  /** Random-access mode fields — not used in standard mode */
  totalRecords?: undefined;
  getRow?: undefined;
  onVisibleRangeChange?: undefined;
}

/** Random-access mode — rows fetched on demand, totalRecords known up front */
export interface UseVirtualRowsRandomAccessOptions extends BaseVirtualRowsOptions {
  renderData?: undefined;
  totalRecords: number;
  getRow: (index: number) => Record<string, unknown> | null;
  onVisibleRangeChange?: (startIndex: number, endIndex: number) => void;
}

export interface UseVirtualRowsReturn {
  /** The flattened row groups (use for mapping to renderers) */
  rowGroups: ReadonlyArray<VirtualRowGroup>;
  /** Lazily resolve a row group by index (avoids O(N) allocation in random-access mode) */
  getRowGroup: (index: number) => VirtualRowGroup;
  /** The virtualizer instance (needed for VirtualTbody measurement) */
  virtualizer: Virtualizer<HTMLElement, Element>;
  /** Total virtual height in px */
  totalSize: number;
  /** Virtual items to render (subset of rowGroups) */
  virtualItems: ReadonlyArray<{
    index: number;
    start: number;
    size: number;
  }>;
  /** Whether virtualization is active */
  isVirtualized: boolean;
}

export function useVirtualRows(
  opts: UseVirtualRowsOptions | UseVirtualRowsRandomAccessOptions,
): UseVirtualRowsReturn {
  const {
    scrollContainerRef,
    enabled,
    estimatedRowHeight,
    overscan,
    expandedRows,
    estimatedSubRowHeight = 47,
    subRowDataField,
    rowIdField = 'id',
  } = opts;

  const isRandomAccess = opts.totalRecords !== undefined;
  const totalRecords = isRandomAccess ? opts.totalRecords : 0;
  const getRow = isRandomAccess ? opts.getRow : undefined;
  const onVisibleRangeChange = isRandomAccess ? opts.onVisibleRangeChange : undefined;
  const renderData = !isRandomAccess ? (opts.renderData ?? []) : [];

  // For random-access mode, we use a lazy proxy instead of allocating N objects.
  // The virtualizer only needs `count` and `estimateSize` — actual row data is
  // resolved at render time via `getRowGroup(index)`.
  const standardRowGroups = useMemo<VirtualRowGroup[]>(() => {
    if (isRandomAccess) return EMPTY_ROW_GROUPS;

    return renderData.map((row, dataIndex) => {
      if (isGroupRow(row)) {
        return {
          dataIndex,
          type: 'group' as const,
          row,
          estimatedHeight: estimatedRowHeight,
        };
      }

      const rec = row as Record<string, unknown>;
      const rowKey = String(rec[rowIdField] ?? dataIndex);
      const isExpanded = expandedRows
        ? expandedRows instanceof Set
          ? expandedRows.has(rowKey)
          : expandedRows.get(rowKey) === true
        : false;

      let height = estimatedRowHeight;
      if (isExpanded && subRowDataField) {
        const subRows = rec[subRowDataField];
        if (Array.isArray(subRows)) {
          height += subRows.length * estimatedSubRowHeight + estimatedSubRowHeight;
        }
      }

      return {
        dataIndex,
        type: 'data' as const,
        row: rec,
        estimatedHeight: height,
      };
    });
  }, [isRandomAccess, renderData, expandedRows, estimatedRowHeight, estimatedSubRowHeight, subRowDataField, rowIdField]);

  const getRowGroupRef = useRef(getRow);
  getRowGroupRef.current = getRow;

  const getRowGroup = useCallback((index: number): VirtualRowGroup => {
    if (isRandomAccess) {
      const row = getRowGroupRef.current!(index);
      return {
        dataIndex: index,
        type: row != null ? 'data' : 'skeleton',
        row,
        estimatedHeight: estimatedRowHeight,
      };
    }
    return standardRowGroups[index];
  }, [isRandomAccess, standardRowGroups, estimatedRowHeight]);

  const rowGroups = isRandomAccess ? EMPTY_ROW_GROUPS : standardRowGroups;

  const estimateSizeFn = useCallback(
    (index: number) => rowGroups[index]?.estimatedHeight ?? estimatedRowHeight,
    [rowGroups, estimatedRowHeight],
  );

  const count = isRandomAccess ? totalRecords : (enabled ? rowGroups.length : 0);

  const virtualizer = useVirtualizer({
    count: enabled ? count : 0,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: estimateSizeFn,
    overscan,
  });

  const prevExpandedRef = useRef(expandedRows);
  useEffect(() => {
    if (prevExpandedRef.current !== expandedRows && enabled) {
      virtualizer.measure();
    }
    prevExpandedRef.current = expandedRows;
  }, [expandedRows, enabled, virtualizer]);

  const rawVirtualItems = virtualizer.getVirtualItems();
  const virtualItems = enabled
    ? rawVirtualItems.map((vItem) => ({
        index: vItem.index,
        start: vItem.start,
        size: vItem.size,
      }))
    : EMPTY_ITEMS;

  // Notify the random-access data layer about visible range changes
  const lastReportedRangeRef = useRef<string>('');
  useEffect(() => {
    if (!enabled || !isRandomAccess || !onVisibleRangeChange || virtualItems.length === 0) return;
    const startIndex = virtualItems[0].index;
    const endIndex = virtualItems[virtualItems.length - 1].index + 1;
    const rangeKey = `${startIndex}-${endIndex}`;
    if (rangeKey !== lastReportedRangeRef.current) {
      lastReportedRangeRef.current = rangeKey;
      onVisibleRangeChange(startIndex, endIndex);
    }
  }, [enabled, isRandomAccess, onVisibleRangeChange, virtualItems]);

  const totalSize = enabled ? virtualizer.getTotalSize() : 0;

  return {
    rowGroups,
    getRowGroup,
    virtualizer,
    totalSize,
    virtualItems,
    isVirtualized: enabled && count > 0,
  };
}
